import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Receipt, MapPin, AlertTriangle, RefreshCw, CheckCircle2, PauseCircle } from 'lucide-react';

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const ITEM_ORDER = ['tier_upgrade', 'workshop', 'live_stream', 'booking', 'spotlight'];

function PlatformCodesCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/admin/tax/platform-codes`, { headers })
      .then(r => { setData(r.data); setDraft(r.data.codes); })
      .catch(() => setData(null));
  }, []);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const r = await axios.put(`${API}/admin/tax/platform-codes`, { codes: draft }, { headers });
      setDraft(r.data.codes);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;
  const dirty = ITEM_ORDER.some(k => draft[k] !== data.codes[k]);

  return (
    <Card className="bg-gray-900/60 border-gray-700" data-testid="platform-tax-codes-card">
      <CardContent className="p-5">
        <h3 className="text-lg font-semibold text-white">How your own offerings are taxed</h3>
        <p className="text-sm text-gray-400 mt-1 max-w-2xl">
          Vendors choose a category for their products. These are yours. States disagree about
          memberships and virtual classes, so if your accountant rules differently, change it here —
          no code change needed.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {ITEM_ORDER.map(key => (
            <div key={key}>
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
                {data.labels[key] || key}
              </label>
              <select
                value={draft[key] || ''}
                onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-md px-3 py-2"
                data-testid={`platform-tax-code-${key}`}
              >
                {data.options.map(o => (
                  <option key={o.tax_code} value={o.tax_code}>{o.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-5">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="save-platform-tax-codes-btn"
          >
            {saving ? 'Saving…' : 'Save categories'}
          </Button>
          {saved && !dirty && (
            <span className="text-sm text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Saved
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminTaxTab() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const qs = `year=${year}${month ? `&month=${month}` : ''}`;
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/tax/status`, { headers }),
        axios.get(`${API}/admin/tax/report?${qs}`, { headers }),
      ]);
      setStatus(s.data);
      setReport(r.data);
    } catch (e) {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="space-y-6" data-testid="admin-tax-tab">
      {/* Collection state */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              {status?.enabled
                ? <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5" />
                : <PauseCircle className="w-6 h-6 text-amber-400 mt-0.5" />}
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Sales tax collection is {status?.enabled ? 'ON' : 'OFF'}
                </h3>
                <p className="text-sm text-gray-400 mt-1 max-w-2xl">
                  {status?.enabled
                    ? 'Tax is calculated at checkout and held on the platform balance for filing. It is never paid out to vendors.'
                    : 'Nothing is being charged to buyers. Turn this on at launch by setting STRIPE_TAX_ENABLED=true in the backend environment.'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={load}
                className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:text-white"
                data-testid="tax-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Registered states</p>
              {status?.registrations?.length ? (
                <div className="flex flex-wrap gap-2" data-testid="tax-registrations">
                  {status.registrations.map(r => (
                    <Badge key={r.id} className="bg-emerald-900/40 text-emerald-200 border-emerald-700/50">
                      {r.state || r.country}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-amber-300">
                  No active registrations found in Stripe. Tax cannot be calculated until you add one.
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Origin address (Stripe)</p>
              {status?.head_office ? (
                <p className="text-sm text-gray-300 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-gray-500" />
                  {status.head_office.line1}, {status.head_office.city} {status.head_office.state} {status.head_office.postal_code}
                </p>
              ) : (
                <p className="text-sm text-amber-300">Not set in Stripe Tax settings.</p>
              )}
            </div>
          </div>

          {status?.vendors_missing_address?.length > 0 && (
            <div
              className="mt-5 p-4 rounded-lg bg-amber-900/20 border border-amber-700/40"
              data-testid="tax-missing-addresses"
            >
              <p className="text-sm font-semibold text-amber-200 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {status.vendors_missing_address.length} vendor
                {status.vendors_missing_address.length === 1 ? '' : 's'} missing a ship-from address
              </p>
              <p className="text-xs text-amber-200/80 mt-1">
                A few states tax based on where the item ships from. These storefronts fall back to your
                origin address until they add theirs in storefront settings.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {status.vendors_missing_address.map(v => (
                  <Badge key={v.vendor_id} className="bg-gray-800 text-gray-300 border-gray-600">
                    {v.store_name || v.vendor_id}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How the platform's own items are taxed */}
      <PlatformCodesCard />

      {/* Filing report */}
      <Card className="bg-gray-900/60 border-gray-700">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Receipt className="w-5 h-5 text-teal-400" /> Tax collected
            </h3>
            <div className="flex items-center gap-2">
              <select
                value={year}
                onChange={e => setYear(Number(e.target.value))}
                className="bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-md px-3 py-1.5"
                data-testid="tax-year-select"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-md px-3 py-1.5"
                data-testid="tax-month-select"
              >
                <option value="">All months</option>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
          </div>

          <div className="p-4 rounded-lg bg-gray-800/60 border border-gray-700 mb-5">
            <p className="text-xs uppercase tracking-wide text-gray-500">Net collected (minus refunds)</p>
            <p className="text-3xl font-bold text-teal-300 mt-1" data-testid="tax-total">
              {money(report?.total_cents)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Add this to what you already collect through Square when you file the same state return.
            </p>
          </div>

          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">By state</p>
                <table className="w-full text-sm" data-testid="tax-by-state-table">
                  <tbody>
                    {report?.by_state?.length ? report.by_state.map(r => (
                      <tr key={r.state} className="border-b border-gray-800">
                        <td className="py-2 text-gray-300">{r.state}</td>
                        <td className="py-2 text-right text-gray-200 font-medium">{money(r.amount_cents)}</td>
                      </tr>
                    )) : (
                      <tr><td className="py-2 text-gray-500" colSpan={2}>No tax recorded for this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">By month</p>
                <table className="w-full text-sm" data-testid="tax-by-month-table">
                  <tbody>
                    {report?.by_month?.length ? report.by_month.map(r => (
                      <tr key={r.month} className="border-b border-gray-800">
                        <td className="py-2 text-gray-300">{r.month}</td>
                        <td className="py-2 text-right text-gray-200 font-medium">{money(r.amount_cents)}</td>
                      </tr>
                    )) : (
                      <tr><td className="py-2 text-gray-500" colSpan={2}>Nothing yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
